/**
 * External EDI validation service connector
 * This module provides integration with third-party EDI validation services
 * DEMO/PLACEHOLDER IMPLEMENTATION - Not connected to real services
 */

import standEancomConfig from './standEancomConfig';

// Available validation service configurations - DEMO ONLY
const VALIDATION_SERVICES = {
  // Single demo validation service that requires API key
  norwegianEdiValidator: {
    name: 'Norwegian EDI Validator (Demo)',
    url: 'https://api.validator.example.com/validate', // Demo URL - not a real endpoint
    requiresApiKey: true,
    supportedStandards: ['EDIFACT', 'EANCOM', 'STAND'],
    supportedMessages: ['ORDERS', 'ORDRSP', 'DESADV', 'INVOIC', 'RECADV'],
    method: 'POST',
    demoMode: true,
    country: 'NO'
  }
};

/**
 * Get the list of available external validation services
 * @returns {Array} List of validation service configurations
 */
export const getAvailableValidationServices = () => {
  return Object.keys(VALIDATION_SERVICES).map(key => ({
    id: key,
    ...VALIDATION_SERVICES[key]
  }));
};

/**
 * Get Norwegian-specific validation services
 * @returns {Array} List of Norwegian validation services
 */
export const getNorwegianValidationServices = () => {
  return Object.keys(VALIDATION_SERVICES)
    .filter(key => VALIDATION_SERVICES[key].country === 'NO')
    .map(key => ({
      id: key,
      ...VALIDATION_SERVICES[key]
    }));
};

/**
 * Validate EDI content using an external validation service
 * @param {string} serviceId - The ID of the validation service to use
 * @param {string} ediContent - The raw EDI content to validate
 * @param {string} apiKey - API key for services that require authentication
 * @param {object} options - Additional options for the validation request
 * @returns {Promise<object>} Validation results from the external service
 */
export const validateWithExternalService = async (serviceId, ediContent, apiKey = null, options = {}) => {
  const service = VALIDATION_SERVICES[serviceId];
  
  if (!service) {
    throw new Error(`Unknown validation service: ${serviceId}`);
  }
  
  if (service.requiresApiKey && !apiKey) {
    throw new Error(`API key required for ${service.name}`);
  }
  
  try {
    // DEMO MODE: Instead of connecting to real services, return mock results
    if (service.demoMode) {
      // Simulate network delay for a more realistic experience
      await new Promise(resolve => setTimeout(resolve, 1500));
      return generateMockValidationResults(serviceId, ediContent, options);
    }
    
    // Real implementation would continue here for actual API connections
    // Build request based on the selected service
    const headers = {
      'Content-Type': 'application/json',
      ...(apiKey && { 'Authorization': `Bearer ${apiKey}` })
    };
    
    // Prepare request payload based on the service requirements
    const payload = preparePayloadForService(serviceId, ediContent, options);
    
    // Make API request to the validation service
    const response = await fetch(service.url, {
      method: service.method,
      headers,
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Validation service error (${response.status}): ${errorText}`);
    }
    
    const result = await response.json();
    
    // Transform the external service response to our standard format
    return transformExternalResults(serviceId, result);
    
  } catch (error) {
    console.error('External validation error:', error);
    return {
      valid: false,
      errors: [{
        type: 'external',
        message: `External validation error: ${error.message}`,
        location: 'service'
      }],
      warnings: [],
      serviceId,
      serviceName: service.name
    };
  }
};

/**
 * Generate mock validation results for demo purposes
 * This simulates what a real validation service might return
 */
const generateMockValidationResults = (serviceId, ediContent, options = {}) => {
  const service = VALIDATION_SERVICES[serviceId];
  
  // Check if UNA segment exists at the beginning of the document
  const hasUNA = ediContent.trimStart().startsWith('UNA');
  
  // Check for UNB segment
  const hasUNB = ediContent.includes('UNB+');
  
  // Detect message type from UNH segment
  let messageType = detectMessageType(ediContent);
  
  // Detect distribution type based on content
  let distributionType = 'STANDARD';
  if (ediContent.includes('NAD+DP')) {
    distributionType = ediContent.includes('GIN+BJ') ? 'CROSSDOCK' : 'DIRECT';
  }
  
  // Create mock results with realistic validation findings
  const mockResults = {
    valid: hasUNA && hasUNB, // In this mock demo, we'll consider it valid if it has proper headers
    errors: [],
    warnings: [],
    stats: {
      total: 6,
      passed: hasUNA && hasUNB ? 5 : 3,
      failed: hasUNA && hasUNB ? 0 : 2,
      warnings: 1
    },
    serviceId,
    serviceName: service.name,
    rawResults: {
      demo: true,
      message: "This is a demonstration of external validation. No actual API calls were made."
    }
  };
  
  // Add some realistic mock errors based on content analysis
  if (!hasUNA) {
    mockResults.errors.push({
      type: 'syntax',
      message: 'Missing UNA service string advice segment',
      location: 'document:start',
      segmentId: 'UNA'
    });
  }
  
  if (!hasUNB) {
    mockResults.errors.push({
      type: 'syntax',
      message: 'Missing UNB interchange header segment',
      location: 'document:start',
      segmentId: 'UNB'
    });
  }
  
  // For Norwegian STAND service, add STAND-specific validations
  if (serviceId === 'norwegianEdiValidator') {
    // Add Norwegian-specific validation results
    addNorwegianStandValidations(mockResults, ediContent, messageType, distributionType);
  } else {
    // Always add a mock warning for demo purposes
    mockResults.warnings.push({
      type: 'demo',
      message: 'DEMO MODE: This is a simulated validation result for demonstration purposes',
      location: 'service'
    });
  }
  
  return mockResults;
};

/**
 * Add Norwegian STAND-specific validation results to the mock results
 */
const addNorwegianStandValidations = (mockResults, ediContent, messageType, distributionType) => {
  // Check STAND compliance
  mockResults.warnings.push({
    type: 'stand',
    message: `Message identified as ${messageType || 'Unknown'} with ${distributionType} distribution type`,
    location: 'document'
  });
  
  // Check for GLN/GTIN format issues
  if (ediContent.includes('NAD+BY+') || ediContent.includes('NAD+SU+')) {
    // Look for GLNs that don't match Norwegian format (usually starting with 70)
    if (!ediContent.match(/NAD\+BY\+70\d{11}/)) {
      mockResults.warnings.push({
        type: 'stand',
        message: 'Buyer GLN may not follow Norwegian numbering convention (70xxxxx)',
        location: 'segment:NAD'
      });
    }
  }
  
  // Check for SSCC in cross-dock scenarios
  if (distributionType === 'CROSSDOCK' && !ediContent.includes('GIN+BJ')) {
    mockResults.errors.push({
      type: 'stand',
      message: 'Cross-dock delivery requires SSCC (GIN+BJ) as per STAND guidelines',
      location: 'document'
    });
  }
  
  // Check for proper message relationships
  if (messageType === 'DESADV' && !ediContent.includes('RFF+ON:')) {
    mockResults.errors.push({
      type: 'stand',
      message: 'Missing reference to order number (RFF+ON) as required by STAND',
      location: 'document'
    });
  }
  
  // Check time requirements based on message type
  if (messageType) {
    const timeLimit = standEancomConfig.getMessageTimeLimit(messageType, distributionType);
    mockResults.warnings.push({
      type: 'stand',
      message: `STAND requires this message to be sent ${timeLimit}`,
      location: 'document'
    });
  }
  
  // Check for bilateral agreements
  if (messageType && distributionType) {
    const agreements = standEancomConfig.getBilateralAgreements(messageType, distributionType);
    if (agreements.length > 0) {
      mockResults.warnings.push({
        type: 'stand',
        message: `STAND recommends ${agreements.length} bilateral agreements for ${messageType} with ${distributionType} distribution`,
        location: 'document',
        details: agreements.slice(0, 3).join(', ') + (agreements.length > 3 ? '...' : '')
      });
    }
  }
};

/**
 * Detect message type from the EDI content
 */
const detectMessageType = (ediContent) => {
  // Look for UNH segment with message type identifier
  const unhMatch = ediContent.match(/UNH\+[^+]+\+([A-Z]{6})/);
  if (unhMatch && unhMatch[1]) {
    // Extract just the message type (first 6 characters of message identifier)
    const messageId = unhMatch[1];
    // Return just the message type part (e.g., "ORDERS", "INVOIC")
    return messageId.substring(0, 6);
  }
  return null;
};

/**
 * Prepare payload for specific validation services
 */
const preparePayloadForService = (serviceId, ediContent, options) => {
  // Create service-specific payloads
  switch (serviceId) {
    case 'norwegianEdiValidator':
      return {
        edi: ediContent,
        standard: 'EANCOM',
        version: 'D96A',
        country: 'NO',
        validateAgainstStand: true,
        checkCrossDock: options.checkCrossDock || false
      };
      
    default:
      return {
        content: ediContent,
        validate: true
      };
  }
};

/**
 * Transform external service results to our standard format
 */
const transformExternalResults = (serviceId, externalResults) => {
  // This would transform various service-specific formats to our standard
  // Since we're in demo mode, we'll just return the mock results directly
  return {
    ...externalResults,
    serviceId,
    serviceName: VALIDATION_SERVICES[serviceId].name
  };
};

export default {
  getAvailableValidationServices,
  getNorwegianValidationServices,
  validateWithExternalService
}; 
